/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Injectable } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';

@Injectable()
export class AdminService {

  constructor(private http: HttpClient) { }

  getAccounts(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/account`
      );
  }

  getActiveAccounts(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/account/active`
      );
  }

  getAccountsWithSKU(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/accountswithsku`
      );
  }

  getAccountWithMeta(accountId: string) {
    return this.http
    .get(
      `/api/v1/admin/account/meta/${accountId}`
    );
  }

  getAccount(accountId: string): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/account/${accountId}`
      );
  }

  saveAccount(accountId: string, update: any): Observable<any> {
    return this.http
      .put(
        `/api/v1/admin/account/${accountId}`,
        update
      );
  }

  getStripePlans(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/stripe/plans`
      );
  }

  getStripePlan(planId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/stripe/plans/${planId}`
      );
  }

  saveStripePlan(plan): Observable<any> {
    return this.http
      .post(
        `/api/v1/admin/stripe/plans`,
        plan
      );
  }

  synchronizeStripe(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/stripe/sync`
      );
  }

  getCoupons(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/stripe/coupons`
      );
  }

  startSubscription(accountId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/stripe/start/${accountId}`
      );
  }

  getWorkflowBatches(workflowId, count, status): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/wfq/${workflowId}?count=${count}&status=${status}`
      );
  }

  getIntegrationLog(requestId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/integration_log/${requestId}`
      );
  }

  resetWorkflowBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/wfq/reset/${batchId}`
      );
  }

  pauseWorkflowBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/wfq/pause/${batchId}`
      );
  }

  getAllFails(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/wfq/fails`
      );
  }

  resetEnrichBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/enrich/reset/${batchId}`
      );
  }

  pauseEnrichBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/enrich/pause/${batchId}`
      );
  }

  getAllEnrichFails(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/enrich/fails`
      );
  }

  getJobsForAccount(accountId: string): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/account/${accountId}`
      );
  }

  getRerunEmailCount(accountId: string, types: string[], startDate: Date, endDate: Date): Observable<any> {
    let options: any = {
      email_types: types,
      start_date: startDate,
      end_date: endDate
    };

    return this.http
      .post(
        `/api/v1/admin/automate_job/reruncount/${accountId}`,
        options
      );
  }

  executeRerun(accountId: string, model: any): Observable<any> {
    return this.http
      .post(
        `/api/v1/admin/automate_job/rerun/${accountId}`,
        model
      );
  }

  updateTasks(updatedSkills: any): Observable<any> {
    return this.http
      .post(
        `/api/v1/admin/automate_job/updatetasks`,
        updatedSkills
      );
  }

  getJob(jobId: string): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/${jobId}`
      );
  }

  getAutomateJobHistory(jobId: string): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/history/${jobId}`
      );
  }

  getAutomateJobAuditHistory(jobId: string): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/audit/${jobId}`
      );
  }

  resetTaskBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/task/reset/${batchId}`
      );
  }

  pauseTaskBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/task/pause/${batchId}`
      );
  }

  splitTaskBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/task/split/${batchId}`
      );
  }

  deleteTaskBatch(batchId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/automate_job/task/delete/${batchId}`
      );
  }

  getAllPausedJobs(){
    return this.http
    .get(
      `/api/v1/admin/automate_job/tasks/paused`
    );
  }

  getAllFailedTasks(accountId?: string, type?: string, status?: number, retry?: number, partner?: string): Observable<any> {
    let params = new HttpParams();
    if (accountId) {
     params =  params.set('accountId', accountId);
    }

    if (type) {
      params = params.set('type', type);
    }

    if (status) {
      params = params.set('status', status.toString());
    }

    if (retry) {
      params = params.set('retries', retry.toString());
    }

    if (partner) {
      params = params.set('partner', partner);
    }

    return this.http
      .get(
        `/api/v1/admin/automate_job/task/fails`, {params: params}
      );
  }

  getEmailDetail(emailDetailId): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/ed/${emailDetailId}`
      );
  }

  createAccount(company, domain, driftOrgId): Observable<any> {
    return this.http
      .post(
        `/api/v1/admin/newaccount`,
        { company: company, domain: domain, driftOrgId: driftOrgId }
      );
  }

  setupDriftOrg(body, driftOrgId: number): Observable<any> {
    return this.http
      .post(
        `/api/v1/admin/driftsetup/${driftOrgId}`,
        body
      );
  }

  getAllDomains(): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/domains`
      );
  }

  getContactCleanupData(emailAddress: string): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/cleanup/contact/${encodeURIComponent(emailAddress)}`
      );
  }

  cleanContact(emailAddress: string, model: any): Observable<any> {
    return this.http
      .post(
        `/api/v1/admin/cleanup/contact/${encodeURIComponent(emailAddress)}`,
        model
      );
  }

  adminExportAccounts(): Observable<any> {
    return this.http
    .get(
      `/api/v1/admin/account/export`
    );
  }

  bingSearch(text: string): Observable<any> {

    return this.http
    .get(
      `/api/v1/admin/bing/search/${encodeURIComponent(text)}`
    );
  }

  getAllInboxes(accountId?: string, enabled?: boolean, authStatus?: number, skuName?: string, botStatus?: boolean, id?: string){
    let params = new HttpParams();
    if (accountId) {
      params =  params.set('accountId', accountId);
    }
    if (enabled != undefined && enabled != null) {
      params =  params.set('enabled', enabled.toString());
    }

    if (authStatus != undefined && authStatus != null) {
      params = params.set('auth_status', authStatus.toString());
    }

    if (skuName && skuName.length > 0) {
      params = params.set('sku_name', skuName);
    }

    if (botStatus != undefined && botStatus != null) {
      params = params.set('is_bot', botStatus.toString());
    }

    if (id && id.length > 0) {
      params = params.set('_id', id);
    }

    return this.http
    .get(
      `/api/v1/admin/inboxes`, {params: params}
    );
  }

  backfillInbox(inboxId: string): Observable<any> {
    return this.http
      .get(
        `/api/v1/admin/inboxes/backfill/${inboxId}`
      );
  }

  removeInbox(accountId: string, emailAddress: string): Observable<any> {
    let options = {
      account_id: accountId,
      email_address: emailAddress
    };

    return this.http
      .post(
        `/api/v1/admin/ops/remove_inbox`,
        options
      );
  }

  removeAccountSource(accountId: string, emailAddress: string): Observable<any> {
    const options = {
      account_id: accountId,
      email_address: emailAddress
    };

    return this.http
      .post(`/api/v1/admin/ops/remove_account_source`,
      options
      );
  }
}



// WEBPACK FOOTER //
// ./client/app/services/admin.service.ts